import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { ClipboardList, Plus, Edit, Trash2, ArrowRight, Loader2, Lock } from 'lucide-react';
import { toast } from 'sonner';
import { QuickNoteGroupDialog, QuickNoteDialog } from './QuickNoteDialogs';
import { useAuth } from '../contexts/AuthContext';

const ADMIN_ROLES = ['system_admin', 'executive', 'operations_manager'];

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LINK_TEST_IDS = { food_pantry: 'food-pantry-quick-notes-link', snap_outreach_nc: 'snap-outreach-quick-notes-link' };

export const QuickNoteRow = ({ note, templateName, onEdit, onDelete, canManage }) => (
  <div className="flex items-start justify-between gap-3 p-2 rounded-md bg-white border" data-testid={`quick-note-row-${note.id}`}>
    <div className="min-w-0">
      <p className="text-sm font-medium flex items-center gap-2">
        {note.name}
        {note.is_builtin && <Badge variant="outline" className="text-[10px]"><Lock className="w-3 h-3 mr-1" />Built-in</Badge>}
      </p>
      <p className="text-xs text-muted-foreground">
        {note.template_ids?.length
          ? note.template_ids.map((id, i) => `${i + 1}. ${templateName(id)}`).join('  ·  ')
          : note.default_description}
      </p>
    </div>
    {canManage && <div className="flex gap-1 shrink-0">
      <Button variant="ghost" size="sm" className="h-7" onClick={onEdit} data-testid={`edit-quick-note-${note.id}`}><Edit className="w-3 h-3" /></Button>
      {!note.is_builtin && (
        <Button variant="ghost" size="sm" className="h-7 text-red-600" onClick={onDelete} data-testid={`delete-quick-note-${note.id}`}><Trash2 className="w-3 h-3" /></Button>
      )}
    </div>}
  </div>
);

const QuickNoteGroupsCard = () => {
  const { user } = useAuth();
  const canManage = ADMIN_ROLES.includes(user?.role);
  const [groups, setGroups] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [caseTypes, setCaseTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [groupDialog, setGroupDialog] = useState({ open: false, group: null });
  const [noteDialog, setNoteDialog] = useState({ open: false, group: null, note: null });

  const load = async () => {
    try {
      const [g, t, ct] = await Promise.all([
        axios.get(`${API}/settings/quick-note-groups`),
        axios.get(`${API}/admin/templates`),
        axios.get(`${API}/admin/templates/case-types`),
      ]);
      setGroups(g.data);
      setTemplates((t.data.templates || []).filter(x => x.is_active !== false));
      setCaseTypes(ct.data.case_types || []);
    } catch (e) {
      toast.error('Failed to load quick notes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);


  const run = async (fn, msg) => {
    setSaving(true);
    try {
      await fn();
      toast.success(msg);
      setGroupDialog({ open: false, group: null });
      setNoteDialog({ open: false, group: null, note: null });
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Something went wrong');
    } finally {
      setSaving(false);
    }
  };

  const saveGroup = (data) => {
    const g = groupDialog.group;
    run(() => g ? axios.put(`${API}/settings/quick-note-groups/${g.id}`, data) : axios.post(`${API}/settings/quick-note-groups`, data),
      g ? 'Group updated' : 'Group created');
  };
  const saveNote = (data) => {
    const { group, note } = noteDialog;
    run(() => note
      ? axios.put(`${API}/settings/quick-note-groups/${group.id}/notes/${note.id}`, data)
      : axios.post(`${API}/settings/quick-note-groups/${group.id}/notes`, data),
      note ? 'Quick note updated' : 'Quick note added');
  };
  const deleteGroup = (g) => window.confirm(`Delete the group "${g.name}" and all its quick notes?`) && run(() => axios.delete(`${API}/settings/quick-note-groups/${g.id}`), 'Group deleted');

  return (
    <Card className="border-dashed" data-testid="quick-note-groups-card">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-3">
          <div>
            <CardTitle className="text-lg flex items-center gap-2">
              <ClipboardList className="w-5 h-5 text-primary" />
              Quick Note Templates
            </CardTitle>
            <CardDescription>Quick note groups by case type. Each quick note creates one case note per assigned template.</CardDescription>
          </div>
          {canManage && (
            <Button size="sm" onClick={() => setGroupDialog({ open: true, group: null })} data-testid="create-quick-note-group-btn">
              <Plus className="w-4 h-4 mr-1" />New Group
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {loading ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : groups.map(g => (
          <div key={g.id} className="rounded-lg border bg-gray-50/60 p-3 space-y-2" data-testid={`quick-note-group-${g.id}`}>
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-semibold text-sm">{g.name}</span>
                <Badge variant="secondary">{g.case_type_name}</Badge>
                {g.is_builtin && <Badge variant="outline" className="text-[10px]">Built-in</Badge>}
                {g.allow_run_all && <Badge className="text-[10px] bg-amber-100 text-amber-800 hover:bg-amber-100" data-testid={`quick-note-group-run-all-badge-${g.id}`}>Run All on</Badge>}
              </div>
              <div className="flex gap-1">
                <Button variant="outline" size="sm" asChild data-testid={LINK_TEST_IDS[g.builtin_key] || `manage-quick-notes-${g.id}`}>
                  <Link to={g.manage_link || `/settings/quick-note-groups/${g.id}`} className="flex items-center gap-1">Manage existing<ArrowRight className="w-3 h-3" /></Link>
                </Button>
                {canManage && (<>
                <Button variant="outline" size="sm" onClick={() => setNoteDialog({ open: true, group: g, note: null })} data-testid={`add-quick-note-${g.id}`}>
                  <Plus className="w-3 h-3 mr-1" />Add Quick Note
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setGroupDialog({ open: true, group: g })} data-testid={`edit-quick-note-group-${g.id}`}><Edit className="w-3 h-3" /></Button>
                </>)}
                {canManage && !g.is_builtin && (
                  <Button variant="ghost" size="sm" className="text-red-600" onClick={() => deleteGroup(g)} data-testid={`delete-quick-note-group-${g.id}`}><Trash2 className="w-3 h-3" /></Button>
                )}
              </div>
            </div>
            {g.existing_notes?.length > 0 && (
              <div className="flex flex-wrap gap-1" data-testid={`quick-note-group-existing-${g.id}`}>
                {g.existing_notes.map(n => (
                  <Badge key={n.id} variant="outline" className={`bg-white ${n.is_active === false ? 'opacity-50' : ''}`}>{n.subject}</Badge>
                ))}
              </div>
            )}
            {g.notes?.length > 0 && (
              <div className="flex flex-wrap gap-1" data-testid={`quick-note-group-notes-${g.id}`}>
                {g.notes.map(n => <Badge key={n.id} variant="outline" className="bg-white" data-testid={`quick-note-tag-${n.id}`}>{n.name}</Badge>)}
              </div>
            )}
            {!g.existing_notes?.length && !g.notes?.length && <p className="text-xs text-muted-foreground">No quick notes yet</p>}
          </div>
        ))}
      </CardContent>
      <QuickNoteGroupDialog open={groupDialog.open} group={groupDialog.group} caseTypes={caseTypes} saving={saving}
        onOpenChange={(o) => setGroupDialog(s => ({ ...s, open: o }))} onSave={saveGroup} />
      <QuickNoteDialog open={noteDialog.open} note={noteDialog.note} templates={templates} saving={saving}
        onOpenChange={(o) => setNoteDialog(s => ({ ...s, open: o }))} onSave={saveNote} />
    </Card>
  );
};

export default QuickNoteGroupsCard;
